/**
 * Sistema de logging estructurado para el frontend
 * Este módulo proporciona funciones para logging consistente en toda la aplicación
 */

import { ErrorTypes, processApiError } from './errorHandler';
import logService from '../services/logService';
import { API_URL } from '../config';

// Niveles de log
export const LogLevels = {
  DEBUG: 'debug',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error',
};

// Determinar el entorno
const IS_PRODUCTION = (process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV) === 'production';

// Obtener configuración de logging desde variables de entorno
const getLogLevel = () => {
  if (IS_PRODUCTION) {
    return process.env.REACT_APP_PROD_LOG_LEVEL || 'warn';
  } else {
    return process.env.REACT_APP_DEV_LOG_LEVEL || 'debug';
  }
};

const getRemoteLoggingEnabled = () => {
  if (IS_PRODUCTION) {
    return process.env.REACT_APP_PROD_ENABLE_REMOTE_LOGGING === 'true';
  } else {
    return process.env.REACT_APP_DEV_ENABLE_REMOTE_LOGGING === 'true';
  }
};

// Configuración del logger
const config = {
  // Nivel mínimo de log que se mostrará
  minLevel: getLogLevel(),

  // Si es true, los logs también se enviarán a un servicio remoto (si está configurado)
  enableRemoteLogging: getRemoteLoggingEnabled(),

  // Si es false, se desactivarán todos los logs (útil para pruebas)
  enabled: true,
  
  // Configuración para envío de logs al backend
  serverLogging: {
    // Deshabilitado temporalmente para evitar bucles infinitos de logging
    enabled: false,
    // Intervalo en milisegundos para enviar logs al backend (30 minutos para reducir frecuencia)
    interval: 30 * 60 * 1000,
    // Número máximo de logs a enviar en cada lote (reducido aún más)
    batchSize: 25,
    // Solo enviar logs críticos al backend para reducir volumen
    levels: [LogLevels.ERROR],
  },
};

// Mapeo de niveles de log a métodos de console
const logMethods = {
  [LogLevels.DEBUG]: console.debug,
  [LogLevels.INFO]: console.info,
  [LogLevels.WARN]: console.warn,
  [LogLevels.ERROR]: console.error,
};

// Orden de prioridad de los niveles de log
const logLevelPriority = {
  [LogLevels.DEBUG]: 0,
  [LogLevels.INFO]: 1,
  [LogLevels.WARN]: 2,
  [LogLevels.ERROR]: 3,
};

/**
 * Determina si un nivel de log debe ser registrado según la configuración actual
 * @param {string} level - Nivel de log
 * @returns {boolean} - True si el log debe ser registrado
 */
const shouldLog = (level) => {
  if (!config.enabled) return false;
  return logLevelPriority[level] >= logLevelPriority[config.minLevel];
};

/**
 * Formatea un mensaje de log con metadatos adicionales
 * @param {string} message - Mensaje principal
 * @param {Object} meta - Metadatos adicionales
 * @returns {Object} - Objeto de log formateado
 */
const formatLogEntry = (message, meta = {}) => {
  return {
    message,
    timestamp: new Date().toISOString(),
    ...meta,
  };
};

// Almacenamiento local de logs para consulta posterior
let logHistory = [];
const MAX_LOG_HISTORY = 1000; // Número máximo de logs a mantener en memoria

// Cola de logs pendientes de enviar al backend
let pendingServerLogs = [];
let serverLogTimer = null;

/**
 * Envía logs a un servicio remoto y/o los almacena localmente
 * @param {string} level - Nivel de log
 * @param {Object} logEntry - Entrada de log formateada
 */
const sendRemoteLog = (level, logEntry) => {
  // Almacenar en el historial local
  const logWithLevel = { level, ...logEntry };
  logHistory.push(logWithLevel);
  
  // Limitar el tamaño del historial
  if (logHistory.length > MAX_LOG_HISTORY) {
    logHistory = logHistory.slice(-MAX_LOG_HISTORY);
  }
  
  // Guardar en localStorage para persistencia entre sesiones
  try {
    const localStorageKey = 'prokids_logs';
    // Solo guardamos los últimos 100 logs en localStorage para evitar problemas de espacio
    const logsToStore = logHistory.slice(-100);
    localStorage.setItem(localStorageKey, JSON.stringify(logsToStore));
  } catch (e) {
    // Ignorar errores de localStorage (puede estar lleno o deshabilitado)
    console.error('Error guardando logs en localStorage:', e);
  }
  
  // Añadir a la cola de logs para enviar al backend si está habilitado
  if (config.serverLogging.enabled && config.serverLogging.levels.includes(level)) {
    pendingServerLogs.push(logWithLevel);
    
    // Iniciar el temporizador para enviar logs si no está ya iniciado
    if (!serverLogTimer) {
      serverLogTimer = setTimeout(sendLogsToServer, config.serverLogging.interval);
    }

    // Si alcanzamos el tamaño máximo del lote, enviar inmediatamente
    if (pendingServerLogs.length >= config.serverLogging.batchSize) {
      if (serverLogTimer) {
        clearTimeout(serverLogTimer);
        serverLogTimer = null;
      }
      sendLogsToServer();
    }
  }
  
  // Enviar a servicio remoto si está configurado
  // Por ejemplo, para errores podríamos usar Sentry:
  if (level === LogLevels.ERROR && window.Sentry) {
    try {
      window.Sentry.captureMessage(logEntry.message, {
        level,
        extra: logEntry,
      });
    } catch (e) {
      // Fallback silencioso si hay un error al enviar a Sentry
      console.error('Error enviando log a Sentry:', e);
    }
  }
};

/**
 * Envía los logs pendientes al servidor
 */
const sendLogsToServer = async () => {
  if (pendingServerLogs.length === 0) {
    serverLogTimer = null;
    return;
  }

  const logsToSend = [...pendingServerLogs];
  pendingServerLogs = [];

  try {
    await logService.sendLogsToServer(logsToSend);
    // Usar console.log directamente para evitar bucles infinitos de logging
    console.log(`[LOGGER] Enviados ${logsToSend.length} logs al servidor`);
  } catch (error) {
    // Si falla, volver a poner los logs en la cola
    pendingServerLogs = [...logsToSend, ...pendingServerLogs];
    console.error('Error enviando logs al servidor:', error);
  }

  // NO reiniciar el temporizador automáticamente para evitar envíos continuos
  // Solo se reiniciará cuando haya nuevos logs que enviar
  serverLogTimer = null;
};

// Enviar logs pendientes cuando la ventana se cierra
window.addEventListener('beforeunload', () => {
  if (pendingServerLogs.length > 0) {
    // Usar fetch con keepalive en lugar de sendBeacon para mejor control de URL
    try {
      // Usar URL absoluta para evitar duplicación con baseURL
      const logsUrl = process.env.NODE_ENV === 'production' ? '/api/logs' : `${API_URL}/logs`;
      fetch(logsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ logs: pendingServerLogs }),
        keepalive: true, // Equivalente a sendBeacon pero con mejor control
      }).catch(() => {
        // Ignorar errores en beforeunload
      });
    } catch (error) {
      // Fallback silencioso si fetch falla
    }
  }
});

/**
 * Inicializa el historial de logs desde localStorage si existe
 */
const initLogHistory = () => {
  try {
    const localStorageKey = 'prokids_logs';
    const storedLogs = localStorage.getItem(localStorageKey);
    if (storedLogs) {
      const parsedLogs = JSON.parse(storedLogs);
      if (Array.isArray(parsedLogs)) {
        logHistory = parsedLogs;
      }
    }
  } catch (e) {
    // Ignorar errores al leer de localStorage
    console.error('Error leyendo logs de localStorage:', e);
  }
};

// Inicializar historial de logs
initLogHistory();

/**
 * Función principal de logging
 * @param {string} level - Nivel de log (debug, info, warn, error)
 * @param {string} message - Mensaje principal
 * @param {Object} meta - Metadatos adicionales
 */
const log = (level, message, meta = {}) => {
  if (!shouldLog(level)) return;

  const logEntry = formatLogEntry(message, meta);
  
  // Log local (consola)
  const logMethod = logMethods[level] || console.log;
  logMethod(`[${level.toUpperCase()}] ${message}`, meta && Object.keys(meta).length ? meta : '');
  
  // Log remoto (si está habilitado)
  if (config.enableRemoteLogging) {
    sendRemoteLog(level, logEntry);
  }
};

/**
 * Registra un mensaje de nivel DEBUG
 * @param {string} message - Mensaje a registrar
 * @param {Object} meta - Metadatos adicionales
 */
export const debug = (message, meta = {}) => {
  log(LogLevels.DEBUG, message, meta);
};

/**
 * Registra un mensaje de nivel INFO
 * @param {string} message - Mensaje a registrar
 * @param {Object} meta - Metadatos adicionales
 */
export const info = (message, meta = {}) => {
  log(LogLevels.INFO, message, meta);
};

/**
 * Registra un mensaje de nivel WARN
 * @param {string} message - Mensaje a registrar
 * @param {Object} meta - Metadatos adicionales
 */
export const warn = (message, meta = {}) => {
  log(LogLevels.WARN, message, meta);
};

/**
 * Registra un mensaje de nivel ERROR
 * @param {string} message - Mensaje a registrar
 * @param {Object} meta - Metadatos adicionales
 */
export const error = (message, meta = {}) => {
  log(LogLevels.ERROR, message, meta);
};

/**
 * Registra un error con toda la información disponible
 * @param {Error|Object} err - Error a registrar
 * @param {string} context - Contexto donde ocurrió el error
 * @param {Object} meta - Metadatos adicionales
 */
export const logError = (err, context = '', meta = {}) => {
  const processedError = processApiError(err);
  
  error(
    `Error en ${context}: ${processedError.message}`,
    {
      errorType: processedError.type || ErrorTypes.UNKNOWN_ERROR,
      errorData: processedError.data,
      originalError: err,
      stack: err && err.stack,
      ...meta,
    },
  );
};

/**
 * Registra el tiempo de ejecución de una función
 * @param {string} name - Nombre de la operación
 * @param {Function} fn - Función a ejecutar y medir
 * @param {Array} args - Argumentos para la función
 * @returns {*} - Resultado de la función
 */
export const logTime = async (name, fn, ...args) => {
  const start = performance.now();
  try {
    const result = await fn(...args);
    const duration = performance.now() - start;
    debug(`${name} completado en ${duration.toFixed(2)}ms`);
    return result;
  } catch (err) {
    const duration = performance.now() - start;
    logError(err, name, { duration: `${duration.toFixed(2)}ms` });
    throw err;
  }
};

/**
 * Configura el logger
 * @param {Object} options - Opciones de configuración
 */
export const configure = (options = {}) => {
  Object.assign(config, options);
};

/**
 * Obtiene el historial de logs almacenado localmente
 * @param {Object} options - Opciones de filtrado
 * @param {string} options.level - Nivel mínimo de logs a obtener
 * @param {number} options.limit - Número máximo de logs a devolver
 * @param {string} options.search - Texto a buscar en los mensajes
 * @returns {Array} - Array de logs filtrados
 */
export const getLogHistory = (options = {}) => {
  const { level, limit = 100, search } = options;
  
  let filteredLogs = [...logHistory];
  
  // Filtrar por nivel
  if (level) {
    const minLevel = logLevelPriority[level] || 0;
    filteredLogs = filteredLogs.filter((log) => {
      return logLevelPriority[log.level] >= minLevel;
    });
  }
  
  // Filtrar por texto de búsqueda
  if (search) {
    const searchLower = search.toLowerCase();
    filteredLogs = filteredLogs.filter((log) => {
      return log.message.toLowerCase().includes(searchLower) ||
        JSON.stringify(log).toLowerCase().includes(searchLower);
    });
  }
  
  // Limitar cantidad
  if (limit && limit > 0) {
    filteredLogs = filteredLogs.slice(-limit);
  }
  
  return filteredLogs;
};

/**
 * Obtiene logs del servidor
 * @param {Object} options - Opciones de filtrado
 * @param {string} options.level - Nivel mínimo de logs a obtener
 * @param {string} options.startDate - Fecha de inicio (formato ISO)
 * @param {string} options.endDate - Fecha de fin (formato ISO)
 * @param {string} options.search - Texto a buscar en los mensajes
 * @param {number} options.limit - Número máximo de logs a devolver
 * @param {number} options.page - Página de resultados (para paginación)
 * @returns {Promise} - Promesa que se resuelve con los logs obtenidos
 */
export const getServerLogs = async (options = {}) => {
  try {
    return await logService.getLogsFromServer(options);
  } catch (error) {
    console.error('Error obteniendo logs del servidor:', error);
    throw error;
  }
};

/**
 * Exporta los logs locales a un archivo descargable
 * @param {Object} options - Opciones de filtrado (igual que getLogHistory)
 * @returns {string} - URL del archivo para descargar
 */
export const exportLocalLogs = (options = {}) => {
  const logs = getLogHistory(options);
  const content = JSON.stringify(logs, null, 2);
  const blob = new Blob([content], { type: 'application/json' });
  return URL.createObjectURL(blob);
};

/**
 * Descarga logs del servidor como archivo
 * @param {Object} options - Opciones de filtrado
 * @param {string} options.level - Nivel de logs (debug, info, warn, error, all)
 * @param {string} options.startDate - Fecha de inicio (formato ISO)
 * @param {string} options.endDate - Fecha de fin (formato ISO)
 * @param {string} options.search - Texto a buscar en los logs
 * @param {string} options.format - Formato del archivo (json o csv)
 * @returns {Promise<string>} - Promesa que se resuelve con la URL del archivo para descargar
 */
export const downloadServerLogs = async (options = {}) => {
  try {
    return await logService.downloadLogsFile(options);
  } catch (error) {
    console.error('Error descargando logs del servidor:', error);
    throw error;
  }
};

/**
 * Limpia el historial de logs
 */
export const clearLogHistory = () => {
  logHistory = [];
  try {
    localStorage.removeItem('prokids_logs');
  } catch (e) {
    // Ignorar errores
  }
};

// Crear el objeto de exportación con todas las comas necesarias
const loggerExport = {
  debug,
  info,
  warn,
  error,
  logError,
  logTime,
  configure,
  LogLevels,
  getLogHistory,
  getServerLogs,
  exportLocalLogs,
  downloadServerLogs,
  clearLogHistory,
};

// Exportar el objeto
export default loggerExport;
